import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Clock,
  X
} from 'lucide-react';
import { SchedulingAssistant } from './SchedulingAssistant';
import { AppointmentEditModal } from './AppointmentEditModal';

// Participant in an appointment (organizer or attendee)
interface Participant {
  id: string;
  type: 'staff' | 'client';
  name: string;
  status?: 'pending' | 'confirmed' | 'declined' | 'tentative';
}

interface Appointment {
  id: string;
  organizer: Participant;
  attendees: Participant[];
  title: string;
  notes?: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no-show';
  created_at: string;
  updated_at: string;
  // Series fields (Phase 7D+)
  series_id?: string | null;
  instance_index?: number | null;
  is_exception?: boolean;
}

interface ClientCalendarViewProps {
  clientId: string;
  clientName: string;
  clientTimezone?: string;
  onBack?: () => void;
}

/**
 * CLIENT CALENDAR VIEW - Phase 7B
 *
 * Outlook-style monthly calendar view for a specific client.
 * Shows appointments, allows navigation, and triggers Scheduling Assistant for booking.
 *
 * Features:
 * - Month grid view with appointment indicators
 * - Navigation between months
 * - Click date to open Scheduling Assistant
 * - "New Appointment" button opens Scheduling Assistant
 */
export const ClientCalendarView: React.FC<ClientCalendarViewProps> = ({
  clientId,
  clientName,
  clientTimezone = 'America/New_York',
  onBack: _onBack
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showSchedulingAssistant, setShowSchedulingAssistant] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [timelineEditAppointment, setTimelineEditAppointment] = useState<Appointment | null>(null);
  const [timelineEditScope, setTimelineEditScope] = useState<'single' | 'series' | undefined>(undefined);

  // Get the first and last day of the current month view
  const getMonthBounds = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);

    // Extend to include full weeks (Sunday start)
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    return { startDate, endDate, firstDay, lastDay };
  };

  // Load appointments for the visible month range
  useEffect(() => {
    loadAppointments();
  }, [clientId, currentDate]);

  // Handle ESC key to close modal
  // Uses CAPTURE phase so it runs before ClientSummaryView's bubble-phase handler
  // SchedulingAssistant also uses capture phase but sets defaultPrevented when it handles selection cancel
  useEffect(() => {
    if (!showSchedulingAssistant) return;

    const handleEscKey = (e: KeyboardEvent) => {
      // Check if event was already handled by SchedulingAssistant (selection cancel)
      if (e.defaultPrevented) return;

      if (e.key === 'Escape') {
        // Stop event from reaching any other handlers
        e.stopImmediatePropagation();
        e.preventDefault();
        setShowSchedulingAssistant(false);
        setSelectedDate(null);
        setTimelineEditAppointment(null);
      }
    };

    // Use capture phase so we run before ClientSummaryView's bubble handler
    // Register with a microtask delay so SchedulingAssistant's handler is registered first
    const timeoutId = setTimeout(() => {
      window.addEventListener('keydown', handleEscKey, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('keydown', handleEscKey, true);
    };
  }, [showSchedulingAssistant]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const { startDate, endDate } = getMonthBounds(currentDate);

      // NOTE: the API filters on attendee_id. Sending client_id here caused every
      // appointment the STAFF organizes to render on any client calendar, and also
      // skipped the staffHasClientAccess check (which only runs when attendee_id is sent).
      const params = new URLSearchParams({
        attendee_id: clientId,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      });

      const response = await fetch(`/api/appointments?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAppointments(data.appointments || []);
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar grid
  const generateCalendarDays = () => {
    const { startDate, endDate, firstDay, lastDay } = getMonthBounds(currentDate);
    const days: { date: Date; isCurrentMonth: boolean; appointments: Appointment[] }[] = [];

    const current = new Date(startDate);
    while (current <= endDate) {
      const dateStr = current.toISOString().split('T')[0];
      const dayAppointments = appointments.filter(apt => {
        const aptDate = new Date(apt.start_time).toISOString().split('T')[0];
        return aptDate === dateStr;
      });

      days.push({
        date: new Date(current),
        isCurrentMonth: current >= firstDay && current <= lastDay,
        appointments: dayAppointments
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  // Format time for display
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: clientTimezone
    });
  };

  // Handle day click
  const handleDayClick = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    setShowSchedulingAssistant(true);
  };

  // Handle new appointment
  const handleNewAppointment = () => {
    setSelectedDate(null); // No pre-selected date
    setShowSchedulingAssistant(true);
  };

  // Handle appointment created (or updated via timeline edit)
  const handleAppointmentCreated = () => {
    setShowSchedulingAssistant(false);
    setSelectedDate(null);
    setTimelineEditAppointment(null);
    setTimelineEditScope(undefined);
    loadAppointments(); // Refresh the calendar
  };

  // Handle appointment click (to edit)
  const handleAppointmentClick = (e: React.MouseEvent, appointment: Appointment) => {
    e.stopPropagation(); // Prevent day cell click from firing
    setEditingAppointment(appointment);
  };

  // Handle appointment updated
  const handleAppointmentUpdated = () => {
    setEditingAppointment(null);
    loadAppointments(); // Refresh the calendar
  };

  // Handle appointment deleted
  const handleAppointmentDeleted = () => {
    setEditingAppointment(null);
    loadAppointments(); // Refresh the calendar
  };

  // Handle "Edit" click from modal - transition to timeline edit mode
  const handleEditInTimeline = (scope: 'single' | 'series' = 'single') => {
    if (!editingAppointment) return;
    console.log('[ClientCalendarView] handleEditInTimeline - scope:', scope, 'series_id:', editingAppointment.series_id);

    // Store the appointment and scope to edit in timeline
    setTimelineEditAppointment(editingAppointment);
    setTimelineEditScope(scope);

    // Set the date from the appointment so SchedulingAssistant initializes with correct date
    const appointmentDate = editingAppointment.start_time.split('T')[0];
    setSelectedDate(appointmentDate);

    // Close the modal
    setEditingAppointment(null);

    // Open the scheduling assistant (it will initialize in edit mode)
    setShowSchedulingAssistant(true);
  };

  const calendarDays = generateCalendarDays();
  const monthYear = currentDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });

  // Check if a date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  return (
    <div className="client-calendar-view">
      {/* Header */}
      <div className="calendar-header">
        <div className="header-left">
          <h2>
            <CalendarIcon size={20} />
            Calendar: {clientName}
          </h2>
          <span className="timezone-badge">
            <Clock size={12} />
            {clientTimezone.replace('_', ' ')}
          </span>
        </div>
        <div className="header-right">
          <button className="btn-new-appointment" onClick={handleNewAppointment}>
            <Plus size={16} />
            New Appointment
          </button>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="month-nav">
        <div className="nav-buttons">
          <button className="nav-btn" onClick={goToPreviousMonth}>
            <ChevronLeft size={18} />
          </button>
          <button className="nav-btn today-btn" onClick={goToToday}>
            Today
          </button>
          <button className="nav-btn" onClick={goToNextMonth}>
            <ChevronRight size={18} />
          </button>
        </div>
        <h3 className="month-title">{monthYear}</h3>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {/* Day Headers */}
        <div className="day-headers">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="day-header">{day}</div>
          ))}
        </div>

        {/* Day Cells */}
        <div className="day-cells">
          {calendarDays.map((day, index) => (
            <div
              key={index}
              className={`day-cell ${!day.isCurrentMonth ? 'other-month' : ''} ${isToday(day.date) ? 'today' : ''}`}
              onClick={() => handleDayClick(day.date)}
            >
              <div className="day-number">{day.date.getDate()}</div>
              <div className="day-appointments">
                {day.appointments.slice(0, 3).map(apt => (
                  <div
                    key={apt.id}
                    className={`appointment-chip ${apt.status}`}
                    title={`${apt.title} - ${formatTime(apt.start_time)} (click to edit)`}
                    onClick={(e) => handleAppointmentClick(e, apt)}
                  >
                    <span className="apt-time">{formatTime(apt.start_time)}</span>
                    <span className="apt-title">{apt.title}</span>
                  </div>
                ))}
                {day.appointments.length > 3 && (
                  <div className="more-appointments">
                    +{day.appointments.length - 3} more
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
        </div>
      )}

      {/* Scheduling Assistant Modal */}
      {showSchedulingAssistant && (
        <div className="scheduling-assistant-overlay">
          <div className="scheduling-assistant-container">
            <button
              className="close-btn"
              onClick={() => {
                setShowSchedulingAssistant(false);
                setSelectedDate(null);
              }}
            >
              <X size={20} />
            </button>
            <SchedulingAssistant
              clientId={clientId}
              clientName={clientName}
              clientTimezone={clientTimezone}
              initialDate={selectedDate || undefined}
              initialEditAppointment={timelineEditAppointment || undefined}
              initialEditScope={timelineEditScope}
              onAppointmentCreated={handleAppointmentCreated}
              onCancel={() => {
                setShowSchedulingAssistant(false);
                setSelectedDate(null);
                setTimelineEditAppointment(null);
                setTimelineEditScope(undefined);
              }}
            />
          </div>
        </div>
      )}

      {/* Appointment Edit Modal */}
      {editingAppointment && (
        <AppointmentEditModal
          appointment={editingAppointment}
          clientName={clientName}
          clientTimezone={clientTimezone}
          isOpen={true}
          onClose={() => setEditingAppointment(null)}
          onUpdated={handleAppointmentUpdated}
          onDeleted={handleAppointmentDeleted}
          onEditInTimeline={handleEditInTimeline}
        />
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .client-calendar-view {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--bg-secondary);
    border-radius: var(--radius-large);
    border: 1px solid var(--border-secondary);
    overflow: hidden;
    position: relative;
  }

  /* Header */
  .calendar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border-secondary);
    background: var(--bg-secondary);
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .header-left h2 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }

  .header-left h2 svg {
    color: var(--accent-green);
  }

  .timezone-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: var(--bg-primary);
    border-radius: 12px;
    font-size: 12px;
    color: var(--text-secondary);
  }

  .btn-new-appointment {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: var(--accent-green);
    color: white;
    border: none;
    border-radius: var(--radius-medium);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-new-appointment:hover {
    background: #059669;
  }

  /* Month Navigation */
  .month-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-bottom: 1px solid var(--border-secondary);
  }

  .nav-buttons {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .nav-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    background: transparent;
    border: 1px solid var(--border);
    border-radius: var(--radius-small);
    color: var(--text-secondary);
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .nav-btn:hover {
    background: var(--bg-primary);
    border-color: var(--accent-green);
    color: var(--accent-green);
  }

  .today-btn {
    padding: 6px 14px;
  }

  .month-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }

  /* Calendar Grid */
  .calendar-grid {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .day-headers {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    background: var(--bg-primary);
    border-bottom: 1px solid var(--border-secondary);
  }

  .day-header {
    padding: 10px;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .day-cells {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    grid-template-rows: repeat(6, 1fr);
    overflow: hidden;
  }

  .day-cell {
    border-right: 1px solid var(--border-secondary);
    border-bottom: 1px solid var(--border-secondary);
    padding: 4px 6px;
    min-height: 80px;
    cursor: pointer;
    transition: background 0.15s;
    overflow: hidden;
  }

  .day-cell:nth-child(7n) {
    border-right: none;
  }

  .day-cell:hover {
    background: var(--bg-primary);
  }

  .day-cell.other-month {
    background: var(--bg-primary);
    opacity: 0.5;
  }

  .day-cell.today {
    background: rgba(16, 185, 129, 0.05);
  }

  .day-cell.today .day-number {
    background: var(--accent-green);
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .day-number {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
    margin-bottom: 4px;
  }

  .day-appointments {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .appointment-chip {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    background: rgba(16, 185, 129, 0.1);
    border-left: 2px solid var(--accent-green);
    border-radius: 2px;
    font-size: 11px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: pointer;
    transition: background 0.15s, transform 0.1s;
  }

  .appointment-chip:hover {
    background: rgba(16, 185, 129, 0.2);
    transform: translateX(2px);
  }

  .appointment-chip.completed {
    background: rgba(107, 114, 128, 0.1);
    border-left-color: #6b7280;
  }

  .appointment-chip.completed:hover {
    background: rgba(107, 114, 128, 0.2);
  }

  .appointment-chip.cancelled {
    background: rgba(239, 68, 68, 0.1);
    border-left-color: #ef4444;
    text-decoration: line-through;
    opacity: 0.6;
  }

  .apt-time {
    font-weight: 500;
    color: var(--text-secondary);
  }

  .apt-title {
    color: var(--text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .more-appointments {
    font-size: 10px;
    color: var(--text-secondary);
    padding: 2px 6px;
  }

  /* Loading Overlay */
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(var(--bg-secondary-rgb), 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-secondary);
    border-top-color: var(--accent-green);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Scheduling Assistant Overlay */
  .scheduling-assistant-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 24px;
  }

  .scheduling-assistant-container {
    position: relative;
    width: 100%;
    max-width: 900px;
    max-height: 90vh;
    background: var(--bg-secondary);
    border-radius: var(--radius-large);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  .close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-primary);
    border: 1px solid var(--border);
    border-radius: 50%;
    color: var(--text-secondary);
    cursor: pointer;
    z-index: 10;
    transition: all 0.15s;
  }

  .close-btn:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
    border-color: var(--accent-green);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .calendar-header {
      flex-direction: column;
      gap: 12px;
      align-items: flex-start;
    }

    .day-cell {
      min-height: 60px;
      padding: 2px 4px;
    }

    .appointment-chip .apt-title {
      display: none;
    }

    .scheduling-assistant-container {
      max-width: 100%;
      max-height: 100vh;
      border-radius: 0;
    }
  }
`;

export default ClientCalendarView;
